import { EmptyState, ErrorState, FreshnessBar, LoadingState, formatDateTime } from "../components/AdminPageStates";
import MetricCard from "../components/MetricCard";
import { useAuth } from "../auth/AuthProvider";
import { useModerationAdmin } from "../hooks/useModerationAdmin";
import { ModerationCaseAction, ModerationTableRow, resolveModerationCase } from "../services/moderationAdminService";
import { useState } from "react";

function ModerationTable({
  actionError,
  emptyMessage,
  isResolving,
  notesByRow,
  onAction,
  onNotesChange,
  rows,
  title
}: {
  actionError: string;
  emptyMessage: string;
  isResolving: (row: ModerationTableRow) => boolean;
  notesByRow: Record<string, string>;
  onAction: (row: ModerationTableRow, action: ModerationCaseAction) => void;
  onNotesChange: (row: ModerationTableRow, notes: string) => void;
  rows: ModerationTableRow[];
  title: string;
}) {
  return (
    <section className="detail-section">
      <h2>{title}</h2>
      {rows.length > 0 ? (
        <div className="table-shell">
          <table className="admin-table moderation-table">
            <thead>
              <tr>
                <th>Report type</th>
                <th>Status</th>
                <th>Category</th>
                <th>Reported target</th>
                <th>Reporter</th>
                <th>Created</th>
                <th>Resolve</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={`${row.source}-${row.id}`}>
                  <td>
                    <strong>{row.reportType}</strong>
                    <span>{row.source}</span>
                  </td>
                  <td>{row.status}</td>
                  <td>{row.category}</td>
                  <td>{row.reportedTarget}</td>
                  <td>{row.reporter}</td>
                  <td>{formatDateTime(row.createdAt)}</td>
                  <td>
                    <div className="moderation-actions">
                      <textarea
                        aria-label={`Resolution notes for ${row.reportedTarget}`}
                        placeholder="Resolution notes"
                        value={notesByRow[`${row.source}-${row.id}`] ?? ""}
                        onChange={(event) => onNotesChange(row, event.target.value)}
                      />
                      <div className="moderation-action-buttons">
                        <button type="button" className="secondary-button" disabled={isResolving(row)} onClick={() => onAction(row, "mark_reviewed")}>
                          Mark Reviewed
                        </button>
                        <button type="button" className="secondary-button" disabled={isResolving(row)} onClick={() => onAction(row, "dismiss")}>
                          Dismiss
                        </button>
                        <button type="button" className="secondary-button danger-button" disabled={isResolving(row)} onClick={() => onAction(row, "remove_content")}>
                          Remove Content
                        </button>
                        <button type="button" className="secondary-button danger-button" disabled={isResolving(row)} onClick={() => onAction(row, "suspend_user")}>
                          Suspend User
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="empty-inline">{emptyMessage}</p>
      )}
      {actionError ? <p className="form-error">{actionError}</p> : null}
    </section>
  );
}

export default function Moderation() {
  const { data, error, isLoading, refresh } = useModerationAdmin();
  const { user } = useAuth();
  const [actionError, setActionError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [notesByRow, setNotesByRow] = useState<Record<string, string>>({});
  const [resolvingRowKey, setResolvingRowKey] = useState("");

  function rowKey(row: ModerationTableRow) {
    return `${row.source}-${row.id}`;
  }

  function handleNotesChange(row: ModerationTableRow, notes: string) {
    setNotesByRow((current) => ({ ...current, [rowKey(row)]: notes }));
  }

  async function handleAction(row: ModerationTableRow, action: ModerationCaseAction) {
    const key = rowKey(row);
    setResolvingRowKey(key);
    setActionError("");
    setActionMessage("");

    try {
      await resolveModerationCase({
        action,
        reviewedBy: user?.uid ?? "unknown_admin",
        reviewedByEmail: user?.email ?? null,
        resolutionNotes: notesByRow[key] ?? "",
        row
      });
      setActionMessage("Moderation case updated.");
      setNotesByRow((current) => ({ ...current, [key]: "" }));
      await refresh();
    } catch {
      setActionError("Unable to update moderation case. Check admin permissions and the target document.");
    } finally {
      setResolvingRowKey("");
    }
  }

  function isResolving(row: ModerationTableRow) {
    return resolvingRowKey === rowKey(row);
  }

  if (isLoading && !data) {
    return <LoadingState message="Loading moderation queue." />;
  }

  if (error) {
    return <ErrorState message={error} />;
  }

  if (!data) {
    return <EmptyState message="No moderation data is available." />;
  }

  return (
    <>
      <FreshnessBar
        dataSource="reports, flaggedContent, reportedMessages"
        isLive={false}
        lastUpdated={data.lastUpdated}
        onRefresh={() => {
          void refresh();
        }}
      />
      {actionMessage ? <p className="success-inline">{actionMessage}</p> : null}

      <div className="overview-grid">
        <MetricCard title="Pending reports" value={String(data.pendingReports.length)} note="Recent reports with reviewStatus pending." />
        <MetricCard title="Pending flagged content" value={String(data.pendingFlaggedContent.length)} note="Recent flaggedContent with status pending." />
        <MetricCard title="Reported messages" value={String(data.reportedMessages.length)} note="Recent reportedMessages records." />
        <MetricCard title="Reported profiles" value={String(data.reportedProfiles.length)} note="Recent reports where targetType is profile." />
        <MetricCard title="Reported posts" value={String(data.reportedPosts.length)} note="Recent reports where targetType is post." />
      </div>

      <ModerationTable
        actionError={actionError}
        title="Pending Reports"
        rows={data.pendingReports}
        isResolving={isResolving}
        notesByRow={notesByRow}
        onAction={(row, action) => void handleAction(row, action)}
        onNotesChange={handleNotesChange}
        emptyMessage="No pending reports were found in recent report history."
      />
      <ModerationTable
        actionError={actionError}
        title="Pending Flagged Content"
        rows={data.pendingFlaggedContent}
        isResolving={isResolving}
        notesByRow={notesByRow}
        onAction={(row, action) => void handleAction(row, action)}
        onNotesChange={handleNotesChange}
        emptyMessage="No pending flagged content was found."
      />
      <ModerationTable
        actionError={actionError}
        title="Reported Messages"
        rows={data.reportedMessages}
        isResolving={isResolving}
        notesByRow={notesByRow}
        onAction={(row, action) => void handleAction(row, action)}
        onNotesChange={handleNotesChange}
        emptyMessage="No reported messages were found."
      />
      <ModerationTable
        actionError={actionError}
        title="Reported Profiles"
        rows={data.reportedProfiles}
        isResolving={isResolving}
        notesByRow={notesByRow}
        onAction={(row, action) => void handleAction(row, action)}
        onNotesChange={handleNotesChange}
        emptyMessage="No reported profiles were found in recent report history."
      />
      <ModerationTable
        actionError={actionError}
        title="Reported Posts"
        rows={data.reportedPosts}
        isResolving={isResolving}
        notesByRow={notesByRow}
        onAction={(row, action) => void handleAction(row, action)}
        onNotesChange={handleNotesChange}
        emptyMessage="No reported posts were found in recent report history."
      />
    </>
  );
}
